import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { formatPrice } from "@/i18n";

export default function WishlistDrawer() {
  const { wishlistOpen, setWishlistOpen, wishlist, removeFromWishlist, moveToCart, currency, t, lang } = useApp();

  return (
    <AnimatePresence>
      {wishlistOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setWishlistOpen(false)}
            className="fixed inset-0 z-[70] bg-ink/40 backdrop-blur-sm"
            data-testid="wishlist-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[440px] bg-paper flex flex-col border-l border-line"
            data-testid="wishlist-drawer"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-line">
              <h2 className="font-display text-3xl">{t.wishlist.title}</h2>
              <button onClick={() => setWishlistOpen(false)} data-testid="close-wishlist-button" aria-label="Close">
                <X className="h-6 w-6" strokeWidth={1.2} />
              </button>
            </div>

            {wishlist.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-10">
                <p className="font-display text-2xl mb-3">{t.wishlist.emptyTitle}</p>
                <p className="text-sm text-ink/50 font-light">{t.wishlist.emptySub}</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto divide-y divide-line" data-testid="wishlist-items">
                  {wishlist.map((p) => (
                    <div key={p.id} className="flex gap-4 p-6" data-testid={`wishlist-item-${p.id}`}>
                      <img src={p.image} alt={p.name} className="h-24 w-20 object-cover bg-surface" />
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-muted">{p.brand}</p>
                        <h3 className="font-display text-xl leading-tight">{p.name}</h3>
                        <p className="text-xs text-ink/50 mt-1">{lang === "ru" ? p.subtitle_ru : p.subtitle_en}</p>
                        <p className="text-sm mt-1">{formatPrice(p.price_rub, currency)}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <button
                            onClick={() => moveToCart(p)}
                            data-testid={`wishlist-move-${p.id}`}
                            className="group inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] link-underline"
                          >
                            {t.wishlist.move} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                          </button>
                          <button
                            onClick={() => removeFromWishlist(p.id)}
                            data-testid={`wishlist-remove-${p.id}`}
                            className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-ink transition-colors link-underline"
                          >
                            {t.wishlist.remove}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-line p-6">
                  <p className="text-[10px] text-muted text-center font-light">{t.wishlist.note}</p>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
